import React from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { TrashIcon } from '@heroicons/react/24/solid'; // Using TrashIcon for this button too
import { courseAPI } from '../services/apiService';

/**
 * ClearSessionButton Component
 * A button that, when clicked, clears all courses and their associated enrollments.
 * It requires user confirmation and provides toast notifications for feedback.
 *
 * @param {object} props - Component props.
 * @param {object} props.user - The authenticated user object, containing the token.
 * @param {function} props.onSessionCleared - Callback function to be executed after a successful session clear.
 * @param {function} props.onRequestConfirmation - Callback to show confirmation modal (optional)
 */
function ClearSessionButton({ user, onSessionCleared, onRequestConfirmation }) {

    const clearAllCourses = async () => {
        try {
            toast.loading("Clearing all data...", { id: "clearSessionToast" });
            // Make a DELETE request to the new backend endpoint
            await courseAPI.clearAll({
                headers: {
                    Authorization: `Bearer ${user?.token}`,
                },
            });
            toast.success("All courses and enrollments cleared successfully!", { id: "clearSessionToast" });
            onSessionCleared(); // Notify parent component to refresh data
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to clear session.", { id: "clearSessionToast" });
            console.error("Error clearing session:", error.response?.data || error);
        }
    };

    const handleClearAllCourses = () => {
        if (onRequestConfirmation) {
            // Use parent's confirmation modal
            onRequestConfirmation({
                title: 'Clear All Data',
                message: 'Are you absolutely sure you want to clear ALL courses and ALL student enrollments? This action cannot be undone!',
                onConfirm: clearAllCourses,
                variant: 'danger'
            });
        } else {
            // Fallback to window.confirm if no modal callback provided
            if (window.confirm("Are you absolutely sure you want to clear ALL courses and ALL student enrollments? This action cannot be undone!")) {
                clearAllCourses();
            }
        }
    };

    return (
        <button
            onClick={handleClearAllCourses}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
            <TrashIcon className="w-4 h-4 mr-2 text-slate-400" />
            Clear All Courses & Enrollments
        </button>
    );
}

export default ClearSessionButton;